import apiClient from './client';
import {
  Check,
  PotentialVendor,
  Vendor,
  CreateVendorRequest,
  UpdateVendorRequest,
} from '../types';

export const vendorsApi = {
  // ============ Vendor CRUD ============

  /**
   * Get all vendors for a client
   */
  getVendors: async (clientId: string): Promise<Vendor[]> => {
    const response = await apiClient.get<Vendor[]>('/vendors', {
      params: { client_id: clientId },
    });
    return response.data;
  },

  /**
   * Create a new vendor
   */
  createVendor: async (clientId: string, data: CreateVendorRequest): Promise<Vendor> => {
    const response = await apiClient.post<Vendor>('/vendors', data, {
      params: { client_id: clientId },
    });
    return response.data;
  },

  /**
   * Update an existing vendor
   */
  updateVendor: async (vendorId: string, data: UpdateVendorRequest): Promise<Vendor> => {
    const response = await apiClient.put<Vendor>(`/vendors/${vendorId}`, data);
    return response.data;
  },

  /**
   * Delete a vendor
   */
  deleteVendor: async (vendorId: string): Promise<void> => {
    await apiClient.delete(`/vendors/${vendorId}`);
  },

  // ============ Vendor Suggestions ============

  /**
   * Get vendor suggestions for a client
   */
  getVendorSuggestions: async (clientId: string, status?: string): Promise<PotentialVendor[]> => {
    const params: Record<string, string> = { client_id: clientId };
    if (status) {
      params.status = status;
    }
    const response = await apiClient.get<PotentialVendor[]>(
      '/vendors/suggestions',
      { params }
    );
    return response.data;
  },

  /**
   * Accept a vendor suggestion
   */
  acceptVendor: async (suggestionId: string): Promise<void> => {
    await apiClient.post(`/vendors/suggestions/${suggestionId}/accept`);
  },

  /**
   * Reject a vendor suggestion
   */
  rejectVendor: async (suggestionId: string): Promise<void> => {
    await apiClient.post(`/vendors/suggestions/${suggestionId}/reject`);
  },

  /**
   * Rename a vendor suggestion
   */
  renameVendor: async (suggestionId: string, newName: string): Promise<void> => {
    await apiClient.put(`/vendors/suggestions/${suggestionId}/name`, { name: newName });
  },

  /**
   * Remove a check from a vendor suggestion
   */
  removeCheckFromVendor: async (suggestionId: string, checkId: string): Promise<void> => {
    await apiClient.delete(`/vendors/suggestions/${suggestionId}/checks/${checkId}`);
  },

  /**
   * Get checks assigned to a specific vendor
   */
  getVendorChecks: async (clientId: string, vendorId: string): Promise<Check[]> => {
    const response = await apiClient.get<Check[]>(
      `/clients/${clientId}/checks`,
      { params: { vendor_id: vendorId } }
    );
    return response.data;
  },

  /**
   * Merge source vendor into target vendor
   */
  mergeVendors: async (sourceVendorId: string, targetVendorId: string): Promise<Vendor> => {
    const response = await apiClient.post<Vendor>('/vendors/merge', {
      source_vendor_id: sourceVendorId,
      target_vendor_id: targetVendorId,
    });
    return response.data;
  },
};
